import { CREATOR_ROUTE } from '~/composables/useAppNavigation'
import { useStoreAdminAccess } from '~/composables/useStoreAdminAccess'
import type { StorePermission } from '~/composables/useOnlineStores'
import {
  hasStorePermission,
  isStoreMember,
  isStorePermission,
} from '~/composables/store/permissions'

declare module '#app' {
  interface PageMeta {
    /** Required store admin grant. Missing = infer from the path. */
    storePermission?: StorePermission
    /**
     * Design-studio pages on the platform host also serve published shops to
     * guests. When true, only *members* without the grant are bounced.
     */
    storePermissionAllowPublic?: boolean
  }
}

/**
 * Block permission-gated store / creator routes *before* the page paints.
 *
 * Creator tools (`/app/creator/stores/...`) require the matching grant.
 * `/store/{id}` on the platform host is the design studio — collaborators
 * without `design` must not see it, even if they arrived via a pasted link.
 * Shoppers on `{shop}.vntru.com` stay on the public storefront.
 *
 * `useOnlineStores` is loaded on demand: a static import from this global
 * middleware put ~400 KB of store editor code in every page's entry chunk.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const guard = inferGuard(to)
  if (!guard) return

  const { permission, allowPublic } = guard
  const onSubdomain = Boolean(currentStoreSubdomain())

  // `{shop}.vntru.com/products/42` is the shop, not the studio. `/store/...`
  // on that same host is still the owner-facing path and stays gated.
  if (onSubdomain && allowPublic && !to.path.startsWith('/store/')) {
    return
  }

  const { ready, loadSession, user, isAuthenticated } = useAuth()
  if (!ready.value) {
    await loadSession()
  }

  // Super-admin `?admin=edit` must send the access header with the fetch.
  // Pass `to` — `useRoute()` inside middleware reads the outgoing page.
  useStoreAdminAccess(to)

  const host = useStorefrontHost()
  const storeId = storeIdFromRoute(to.params, host.value.storeId)
  if (!storeId) {
    return deny(onSubdomain)
  }

  const { useOnlineStores } = await import('~/composables/useOnlineStores')
  const { peekById, fetchOwnedById, isStudioDraft } = useOnlineStores()
  let store = peekById(storeId, to)

  // The dashboard list is the published store. Fetch the draft once, then
  // reuse it — every later /store navigation was sending GET /online-stores/:id again.
  if (isAuthenticated.value && !isStudioDraft(storeId)) {
    try {
      store = (await fetchOwnedById(storeId)) ?? store
    }
    catch {
      // Keep the cached copy; a transport error must not flash the page.
    }
  }

  if (isStoreMember(store, user.value)) {
    if (hasStorePermission(store, user.value, permission)) return
    return deny(onSubdomain)
  }

  if (allowPublic) return

  return deny(onSubdomain)
})

function inferGuard(to: { path: string, meta: object }): {
  permission: StorePermission
  allowPublic: boolean
} | null {
  const meta = to.meta as { storePermission?: unknown, storePermissionAllowPublic?: unknown }
  if (isStorePermission(meta.storePermission)) {
    return {
      permission: meta.storePermission,
      allowPublic: Boolean(meta.storePermissionAllowPublic),
    }
  }

  const path = to.path.replace(/\/$/, '') || '/'
  if (/^\/app\/creator\/stores\/[^/]+\/orders(?:\/|$)/.test(path)) {
    return { permission: 'orders', allowPublic: false }
  }
  if (/^\/app\/creator\/stores\/[^/]+\/financial(?:\/|$)/.test(path)) {
    return { permission: 'financial', allowPublic: false }
  }
  if (/^\/app\/creator\/stores\/[^/]+\/(?:inventory|categories)(?:\/|$)/.test(path)) {
    return { permission: 'inventory', allowPublic: false }
  }
  if (/^\/app\/creator\/stores\/[^/]+\/(?:configuration|admins)(?:\/|$)/.test(path)) {
    return { permission: 'configuration', allowPublic: false }
  }
  if (/^\/app\/creator\/stores\/[^/]+\/messages(?:\/|$)/.test(path)) {
    return { permission: 'messages', allowPublic: false }
  }
  if (path === '/publish' || /^\/store\/[^/]+\/publish(?:\/|$)/.test(path)) {
    return { permission: 'publish', allowPublic: false }
  }
  if (path.startsWith('/checkout') || /^\/store\/[^/]+\/checkout(?:\/|$)/.test(path)) {
    return null
  }
  if (/^\/store\/[^/]+(?:\/|$)/.test(path)) {
    return { permission: 'design', allowPublic: true }
  }
  return null
}

function storeIdFromRoute(
  params: Record<string, unknown>,
  hostStoreId: string | null | undefined,
): string {
  const raw = params.storeId ?? params.id
  const fromParams = String(Array.isArray(raw) ? raw[0] : raw || '')
  return fromParams || String(hostStoreId || '')
}

function deny(onSubdomain: boolean) {
  return navigateTo(onSubdomain ? '/' : CREATOR_ROUTE, { replace: true })
}
